import {
  HexCoord,
  ChunkCoord,
  CHUNK_SIZE,
  HEX_COL_SPACING,
  HEX_ROW_SPACING,
  HEX_ROW_OFFSET,
  TILE_RENDER_WIDTH,
  TILE_RENDER_HEIGHT,
} from './types'

export function hexToPixel(q: number, r: number): { x: number; y: number } {
  // Flat-top hex grid: odd columns shift down by half a tile
  return {
    x: q * HEX_COL_SPACING,
    y: r * HEX_ROW_SPACING + (q & 1) * HEX_ROW_OFFSET,
  }
}

export function pixelToHex(px: number, py: number): HexCoord {
  // Flat-top hex grid: undo column stagger before dividing
  const q = Math.round(px / HEX_COL_SPACING)
  const r = Math.round((py - (q & 1) * HEX_ROW_OFFSET) / HEX_ROW_SPACING)
  return { q, r }
}

export function hexToChunk(q: number, r: number): ChunkCoord {
  return {
    cx: Math.floor(q / CHUNK_SIZE),
    cy: Math.floor(r / CHUNK_SIZE),
  }
}

export function chunkToHexRange(cx: number, cy: number): { minQ: number; minR: number; maxQ: number; maxR: number } {
  return {
    minQ: cx * CHUNK_SIZE,
    minR: cy * CHUNK_SIZE,
    maxQ: (cx + 1) * CHUNK_SIZE - 1,
    maxR: (cy + 1) * CHUNK_SIZE - 1,
  }
}

export function chunkKey(cx: number, cy: number): string {
  return `${cx},${cy}`
}

export function hexDistance(a: HexCoord, b: HexCoord): number {
  return Math.abs(a.q - b.q) + Math.abs(a.r - b.r)
}

export function getHexNeighbors(q: number, r: number): HexCoord[] {
  // Flat-top odd-q offset: 6 neighbors differ by column parity
  const odd = (q & 1) === 1
  return odd
    ? [
        { q: q,     r: r - 1 }, // top
        { q: q + 1, r: r },     // top-right  (even col, above stagger)
        { q: q + 1, r: r + 1 }, // bottom-right
        { q: q,     r: r + 1 }, // bottom
        { q: q - 1, r: r + 1 }, // bottom-left
        { q: q - 1, r: r },     // top-left   (even col, above stagger)
      ]
    : [
        { q: q,     r: r - 1 }, // top
        { q: q + 1, r: r - 1 }, // top-right  (odd col, below stagger)
        { q: q + 1, r: r },     // bottom-right
        { q: q,     r: r + 1 }, // bottom
        { q: q - 1, r: r },     // bottom-left
        { q: q - 1, r: r - 1 }, // top-left   (odd col, above stagger)
      ]
}

/** Reusable buffer for viewportToChunks — avoids GC pressure from allocation per frame. */
const _vtcBuffer: ChunkCoord[] = []

/**
 * @param zoomY - Effective zoom for Y axis (e.g. zoom * isometricScale). If omitted, uses zoom (uniform scale).
 */
export function viewportToChunks(
  cameraX: number,
  cameraY: number,
  zoom: number,
  screenWidth: number,
  screenHeight: number,
  buffer: number,
  zoomY?: number,
): ChunkCoord[] {
  const effectiveZoomY = zoomY ?? zoom
  const invZoom = 1 / zoom
  const invZoomY = 1 / effectiveZoomY
  const left = -cameraX * invZoom
  const right = (screenWidth - cameraX) * invZoom
  const top = -cameraY * invZoomY
  const bottom = (screenHeight - cameraY) * invZoomY

  const marginX = TILE_RENDER_WIDTH
  const marginY = TILE_RENDER_HEIGHT + HEX_ROW_OFFSET
  const minCx = Math.floor(Math.floor((left - marginX) / HEX_COL_SPACING) / CHUNK_SIZE) - buffer
  const maxCx = Math.floor(Math.ceil((right + marginX) / HEX_COL_SPACING) / CHUNK_SIZE) + buffer
  const minCy = Math.floor(Math.floor((top - marginY) / HEX_ROW_SPACING) / CHUNK_SIZE) - buffer
  const maxCy = Math.floor(Math.ceil((bottom + marginY) / HEX_ROW_SPACING) / CHUNK_SIZE) + buffer

  // Reuse buffer to avoid array + object allocation every call
  let idx = 0
  for (let cx = minCx; cx <= maxCx; cx++) {
    for (let cy = minCy; cy <= maxCy; cy++) {
      if (idx < _vtcBuffer.length) {
        _vtcBuffer[idx].cx = cx
        _vtcBuffer[idx].cy = cy
      } else {
        _vtcBuffer.push({ cx, cy })
      }
      idx++
    }
  }
  _vtcBuffer.length = idx
  return _vtcBuffer
}
